import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import React from 'react';
import type { StateCreator } from 'zustand';
import { create, useStore } from 'zustand';

export function createContextStore<StateType, ActionType>(
  initializer: StateCreator<StateType & ActionType>,
  autoSetState = true,
) {
  type StoreType = StateType & ActionType;

  function createStore(state: StateType) {
    return create<StoreType>()((set, get, store) => {
      const actions = initializer(set, get, store);

      return {
        ...actions,
        ...state,
      };
    });
  }

  const StoreContext = React.createContext<ReturnType<typeof createStore> | undefined>(undefined);

  function StoreProvider(
    props: React.PropsWithChildren<{
      state: StateType;
    }>,
  ) {
    const initialState = React.useRef(props.state);
    const [store] = React.useState(() => {
      return createStore(props.state);
    });

    useIsomorphicLayoutEffect(() => {
      if (initialState.current !== props.state && autoSetState) {
        store.setState(props.state as Partial<StoreType>);
      }
    }, [props.state, store, autoSetState]);

    return <StoreContext.Provider value={store}>{props.children}</StoreContext.Provider>;
  }

  function useStoreSelector<T>(selector: (store: StoreType) => T) {
    const storeContext = React.useContext(StoreContext);
    if (!storeContext) {
      throw new Error(`useStore must be used within StoreProvider`);
    }

    return useStore(storeContext, selector);
  }

  return [StoreProvider, useStoreSelector] as const;
}
